/**
 * sl-suoi.1.3: Hot Sync Initialization Service
 *
 * Injects priority doc IDs into the sync engine after workspace initialization.
 * For cloud workspaces, the sync engine starts in selective mode (no initial docs).
 * This service adds Recent 5 docs + the currently open doc as high-priority items.
 *
 * SOLID:
 * - S: Only handles initial doc priority injection
 * - D: Depends on abstractions (WorkspaceService, RecentDocsService)
 */
import { DebugLogger } from '@affine/debug';
import { OnEvent, Service } from '@toeverything/infra';

import { RecentDocsService } from '../../quicksearch';
import {
  type Workspace,
  WorkspaceInitialized,
  WorkspaceService,
} from '../../workspace';

const logger = new DebugLogger('HotSyncInit');

@OnEvent(WorkspaceInitialized, s => s.onWorkspaceInitialized)
export class HotSyncInitService extends Service {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly recentDocsService: RecentDocsService
  ) {
    super();
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    const workspace = this.workspaceService.workspace;
    if (workspace.flavour === 'local') {
      return; // Local workspaces use eager sync
    }

    const engine = workspace.engine;
    const recentDocIds = this.recentDocsService.getRecentDocIds();

    logger.info(`Injecting ${recentDocIds.length} recent docs into hot sync`);

    // Add recent docs with high priority so they sync first
    for (const docId of recentDocIds) {
      engine.doc.addDoc(docId, 'hot-sync-recent');
      engine.doc.addPriority(docId, 10);
    }

    // The currently open doc will be added by the workbench when it opens.
    // This service only handles the initial recent docs injection.
  }
}
